import JSZip from 'jszip';
import { useEffect, useMemo, useState } from 'react';
import { useQuery, useSubscriber } from 'services/api/hooks';
import {
  downloadFileFromBlobUrl,
  downloadFileFromUrl,
  getFileNameWithExtension
} from 'core/services/storage/utils';
import { useBrowserTabNotification } from 'services/notifications/hooks/useBrowserTabNotification';
import {
  getAttachmentsByConversation,
  getConversation,
  getConversationsByCampaign
} from './api';
import {
  onCreateConversation,
  onCreateMessage,
  onUpdateConversation
} from './subscriptions';
import { Attachment, Conversation, Message } from './types';
import { getMediaFileName } from 'modules/conversations/utils';
import { useGetMediaDownloadUrlLazyQuery } from 'generated-graphql/hooks/apollo-hooks';

type UseConversationsInput = {
  campaignId?: string;
  queryParams?: Record<string, string | unknown>;
};

const useCampaignConversations = ({
  campaignId,
  queryParams = {}
}: UseConversationsInput) => {
  const [conversationsList, setConversationsList] = useState<Conversation[]>(
    []
  );

  const { setNotificationTo, clearNotification } = useBrowserTabNotification();

  useEffect(() => {
    const conversationNeedsReview = conversationsList.filter(
      (conversation) => conversation.needsReview
    );

    setNotificationTo(conversationNeedsReview.length);

    return () => {
      clearNotification();
    };
  }, [JSON.stringify(conversationsList)]);

  const params: {
    subParams: Partial<Record<string, { eq: string }>>;
    queryParams: Partial<Record<string, string>>;
    query: (params: any) => Promise<any>;
  } = useMemo(() => {
    if (campaignId) {
      return {
        subParams: {
          campaignId: { eq: campaignId }
        },
        queryParams: { campaignId, ...queryParams },
        query: getConversationsByCampaign
      };
    }

    return {
      subParams: { campaignId: { eq: '' } },
      queryParams: { campaignId: '' },
      query: (params: any): Promise<any> => {
        return new Promise((resolve) => resolve(params));
      }
    };
  }, [campaignId, JSON.stringify(queryParams)]);

  const { refetch } = useQuery<{ items: Conversation[] }>(
    params.query,
    params.queryParams,
    {
      onFetchSuccess: (data) => {
        setConversationsList(data?.items);
      }
    }
  );

  const updateConversation = (updatedConversation: Conversation) => {
    setConversationsList((prevConversations) => {
      // Find the index of the conversation in the array.
      const index = prevConversations.findIndex(
        (conversation) => conversation.id === updatedConversation.id
      );

      if (index !== -1) {
        // Replace the conversation at the found index with the updated conversation.
        prevConversations[index] = updatedConversation;
        // Return a new array for immutability.
        return [...prevConversations];
      }

      return [...prevConversations, updatedConversation];
    });
  };

  const fetchAndUpdateConversation = async (conversationId: string) => {
    const updatedConversation = await getConversation({ id: conversationId });
    updateConversation(updatedConversation);
  };

  useSubscriber({
    subQuery: onCreateMessage,
    subParams: params.subParams,
    onFetchSuccess: (data) => {
      const message: Message = data?.onCreateMessage;

      if (message.conversationId) {
        fetchAndUpdateConversation(message.conversationId);
      }
    }
  });

  useSubscriber({
    subQuery: onCreateConversation,
    subParams: params.subParams,
    onFetchSuccess: (data) => {
      const conversation: Conversation = data?.onCreateConversation;

      if (conversation.id) {
        fetchAndUpdateConversation(conversation.id);
      }
    }
  });

  useSubscriber({
    subQuery: onUpdateConversation,
    subParams: params.subParams,
    onFetchSuccess: (data) => {
      const conversation: Conversation = data?.onUpdateConversation;

      if (conversation.id) {
        fetchAndUpdateConversation(conversation.id);
      }
    }
  });

  return { conversations: conversationsList, updateConversation, refetch };
};

const useConversationAttachments = (
  conversationId: string,
  {
    recipientName
  }: {
    recipientName: string;
  }
) => {
  const [isDownloading, setIsDownloading] = useState(false);

  const { data, isLoading } = useQuery<{ items: Attachment[] }>(
    getAttachmentsByConversation,
    { conversationId }
  );

  const [getMediaDownloadUrl] = useGetMediaDownloadUrlLazyQuery();

  const attachments = data?.items || [];

  const downloadFile = async ({
    attachmentId,
    fileName
  }: {
    attachmentId: string;
    fileName: string;
  }) => {
    const { data } = await getMediaDownloadUrl({
      variables: {
        attachmentId
      }
    });

    const downloadUrl = data?.getMediaDownloadUrl?.url as string;
    downloadFileFromUrl(downloadUrl, fileName);
  };

  const downloadAllFiles = async () => {
    const zip = new JSZip();

    const downloadPromises = attachments.map(async (attachment) => {
      const { data } = await getMediaDownloadUrl({
        variables: {
          attachmentId: attachment.id
        }
      });

      const downloadUrl = data?.getMediaDownloadUrl?.url as string;
      const response = await fetch(downloadUrl);
      const blob = await response.blob();

      const fileNameWithExtension = getFileNameWithExtension(
        getMediaFileName(attachment.media),
        attachment.media.contentType
      );

      zip.file(fileNameWithExtension, blob);
    });

    setIsDownloading(true);

    await Promise.all(downloadPromises);

    const content = await zip.generateAsync({ type: 'blob' });
    downloadFileFromBlobUrl(
      URL.createObjectURL(content),
      `${recipientName.trim()}.zip`
    );

    setIsDownloading(false);
  };

  return {
    attachments,
    downloadFile,
    downloadAllFiles,
    isLoading,
    isDownloading
  };
};

export { useCampaignConversations, useConversationAttachments };
